import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Trophy,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PreviewBanner } from "@/components/PreviewBanner";
import {
  type HandSummary,
  type HandAnnotation,
  analyzeHand,
} from "@/features/learn/hand-annotations";

// ---------------------------------------------------------------------------
// Mock data — 3 sample hands to demonstrate the UI
// ---------------------------------------------------------------------------

const MOCK_HANDS: HandSummary[] = [
  {
    position: "BTN",
    holeCards: ["As", "Kh"],
    actions: [
      { phase: "preflop", action: "raise", amount: 60 },
      { phase: "flop", action: "check" },
      { phase: "turn", action: "bet", amount: 80 },
      { phase: "river", action: "bet", amount: 150 },
    ],
    potSize: 450,
    bb: 20,
    won: true,
    netResult: 290,
    boardCards: ["Ks", "7d", "2c", "9h", "3s"],
  },
  {
    position: "BB",
    holeCards: ["9d", "6s"],
    actions: [{ phase: "preflop", action: "fold" }],
    potSize: 40,
    bb: 20,
    won: false,
    netResult: -20,
  },
  {
    position: "UTG",
    holeCards: ["5h", "3d"],
    actions: [
      { phase: "preflop", action: "call", amount: 20 },
      { phase: "flop", action: "call", amount: 200 },
    ],
    potSize: 520,
    bb: 20,
    won: false,
    netResult: -220,
    boardCards: ["Qh", "Jh", "8d", "2s", "Ah"],
  },
];

// ---------------------------------------------------------------------------
// Small sub-components
// ---------------------------------------------------------------------------

const CardBadge = ({ card }: { card: string }) => {
  const rank = card.slice(0, -1);
  const suitChar = card.slice(-1);
  const isRed = suitChar === "h" || suitChar === "d";
  const suitSymbol =
    suitChar === "s"
      ? "\u2660"
      : suitChar === "h"
        ? "\u2665"
        : suitChar === "d"
          ? "\u2666"
          : "\u2663";

  return (
    <span
      className={`inline-flex items-center justify-center w-9 h-12 rounded-md border border-border bg-card text-sm font-bold ${isRed ? "text-red-400" : "text-foreground"}`}
    >
      {rank}
      {suitSymbol}
    </span>
  );
};

const PositionBadge = ({ position }: { position: string }) => (
  <span className="inline-flex items-center rounded-full bg-secondary/20 px-2 py-0.5 text-[11px] font-semibold text-secondary-foreground">
    {position}
  </span>
);

const ResultBadge = ({ amount }: { amount: number }) => (
  <span
    className={`text-lg font-bold tabular-nums ${amount >= 0 ? "text-green-400" : "text-red-400"}`}
  >
    {amount >= 0 ? "+" : ""}
    {amount}
  </span>
);

// ---------------------------------------------------------------------------
// Annotation bubble
// ---------------------------------------------------------------------------

interface AnnotationBubbleProps {
  annotation: HandAnnotation;
  lang: string;
}

const AnnotationBubble = ({ annotation, lang }: AnnotationBubbleProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEs = lang.startsWith("es");
  const title = isEs ? annotation.titleEs : annotation.titleEn;
  const message = isEs ? annotation.messageEs : annotation.messageEn;
  const isPositive = annotation.pattern === "won_big_pot";

  return (
    <div
      className={`rounded-lg border p-3 ${isPositive ? "border-green-500/30 bg-green-500/5" : "border-primary/30 bg-primary/5"}`}
    >
      <div className="flex items-start gap-2">
        {isPositive ? (
          <Trophy className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
        ) : (
          <AlertTriangle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-xs text-muted-foreground mt-1">{message}</p>
          {annotation.lessonId !== "general" && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 mt-2 text-xs text-primary"
              onClick={() => navigate(`/learn/${annotation.lessonId}`)}
            >
              <BookOpen className="w-3 h-3 mr-1" />
              {t("hand_review_study")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Hand card
// ---------------------------------------------------------------------------

interface HandCardProps {
  hand: HandSummary;
  index: number;
  lang: string;
}

const HandCard = ({ hand, index, lang }: HandCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const annotations = analyzeHand(hand);

  return (
    <div className="glass overflow-hidden">
      <button
        className="w-full p-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-muted-foreground w-5 shrink-0">
            #{index + 1}
          </span>
          <div className="flex gap-1">
            {hand.holeCards.map((c) => (
              <CardBadge key={c} card={c} />
            ))}
          </div>
          <PositionBadge position={hand.position} />
          <div className="flex-1" />
          <ResultBadge amount={hand.netResult} />
          {annotations.length > 0 &&
            (expanded ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ))}
        </div>
      </button>

      {expanded && annotations.length > 0 && (
        <div className="px-4 pb-4 space-y-2 border-t border-border/50 pt-3">
          {annotations.map((a) => (
            <AnnotationBubble key={a.pattern} annotation={a} lang={lang} />
          ))}
        </div>
      )}

      {expanded && annotations.length === 0 && (
        <div className="px-4 pb-4 border-t border-border/50 pt-3">
          <p className="text-xs text-muted-foreground text-center">
            {lang.startsWith("es")
              ? "Sin observaciones para esta mano."
              : "No observations for this hand."}
          </p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Summary section — unique lessons referenced
// ---------------------------------------------------------------------------

function collectAreas(hands: HandSummary[]): HandAnnotation[] {
  const seen = new Set<string>();
  const result: HandAnnotation[] = [];
  for (const hand of hands) {
    for (const a of analyzeHand(hand)) {
      if (a.pattern === "won_big_pot") continue; // skip positive reinforcement in summary
      if (!seen.has(a.lessonId)) {
        seen.add(a.lessonId);
        result.push(a);
      }
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

const HandReview = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;

  const areas = collectAreas(MOCK_HANDS);

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-5 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 pt-1">
          <Button
            variant="ghost"
            size="icon"
            className="-ml-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold tracking-tight">
              {t("hand_review_title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("hand_review_subtitle")}
            </p>
          </div>
        </div>

        {/* This is illustrative data, not the user's real hand history — see
            apps/smartpoker/src/components/PreviewBanner.tsx for the convention. */}
        <PreviewBanner
          titleKey="hand_review_preview_title"
          descriptionKey="hand_review_preview_desc"
        />

        {/* Hand list */}
        <div className="space-y-3">
          {MOCK_HANDS.map((hand, i) => (
            <HandCard key={i} hand={hand} index={i} lang={lang} />
          ))}
        </div>

        {/* Areas to review */}
        {areas.length > 0 && (
          <div className="glass p-4 space-y-3">
            <h2 className="text-sm font-bold">
              {t("hand_review_areas")}
            </h2>
            <div className="flex flex-wrap gap-2">
              {areas.map((a) => (
                <button
                  key={a.lessonId}
                  onClick={() => navigate(`/learn/${a.lessonId}`)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20 transition-colors"
                >
                  <BookOpen className="w-3 h-3" />
                  {lang.startsWith("es") ? a.titleEs : a.titleEn}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HandReview;
